/**
 * CMP-11, solved on a pattern-density map.
 *
 * The closed-form polish treats the die as a handful of independent regions:
 * each one clears on its own two-phase formula, dishes and erodes by its own
 * fitted law, and no region knows its neighbours exist. That is a real model
 * and it orders the failure modes correctly - but the entire subject of
 * pattern-density CMP is that regions are NOT independent. The pad is a
 * millimetre-scale elastic body: it bridges from a dense array onto the open
 * field beside it, robs pressure from one and lends it to the other, and the
 * same block of layout polishes differently depending on what it sits next to.
 * A model without a pad length cannot say any of that, however well fitted.
 *
 * Here the die is a two-dimensional height map, stepped through time:
 *
 *   THE PAD IS A KERNEL. Each step, the pad's reference surface is the die
 *   surface smoothed over the planarisation length (~2.5 mm - polishing pads
 *   are millimetre-stiff and micron-soft). Local pressure rises where the
 *   surface stands proud of that envelope and falls where it sits recessed,
 *   under a fixed total force. High spots polish fast BECAUSE they are high:
 *   planarisation is the feedback, not an efficiency coefficient.
 *
 *   TWO SCALES, HONESTLY SPLIT. The grid resolves the die (~170 um cells,
 *   about what the pad can distinguish). The line/space texture inside a cell
 *   is below pad resolution, so within a cell the solver uses the same
 *   contact-fraction and pad-reach laws the closed form uses - the point of
 *   the grid is not to re-derive those, it is to stop assuming every region
 *   lives alone. Dishing stays a sub-grid law; WHERE and HOW LONG it acts
 *   comes from the grid.
 *
 * What that buys, none of which a region list can express: a clearing map
 * with a sequence and an address, shorts as places instead of a boolean,
 * erosion that depends on the neighbourhood, and a step-height-versus-time
 * curve whose decay length is the pad's - measurable, and different for a
 * stiff pad and a floppy one.
 */

import { clamp } from '../../core/math'
import {
  padReach,
  pressureAt,
  prestonCoefficient,
  recessedMetalFraction,
  relativeVelocity,
  selectivityOf,
  NOMINAL_CMP_CONDITION,
  type CmpCondition,
  type CmpRecipe,
  type PatternRegion,
} from './CmpSim'

// ------------------------------------------------------------- calibration

/** Die edge [mm]. One die; the wafer-scale profile stays with the closed form. */
const DIE_MM = 16
/** Default planarisation length [mm] - where a production IC1000-class pad sits. */
export const PLANARIZATION_LENGTH_MM = 2.5
/**
 * Pad compliance amplitude [nm]: the height excess that doubles local
 * pressure. Sets how hard the pad punishes a high spot; a few hundred
 * nanometres is the scale over which real pads redistribute load.
 */
const COMPLIANCE_NM = 250
/** Same endpoint physics as the closed form: bulk transition, not last spot. */
const ENDPOINT_CLEARED_FRACTION = 0.7

// ------------------------------------------------------------------- die

/** A rectangular placement of one pattern region on the die, cell units. */
export interface RegionBlock {
  region: PatternRegion
  x0: number
  y0: number
  x1: number
  y1: number
}

export interface DieMap {
  n: number
  cellUm: number
  /** Metal trench density per cell. */
  density: Float64Array
  /** Line width per cell [um]. */
  lineWidthUm: Float64Array
  /** Index into `regions` per cell, -1 for unpatterned field. */
  regionIndex: Int16Array
  regions: PatternRegion[]
}

/** Build a die from explicit block placements over an unpatterned field. */
export function dieFromBlocks(blocks: RegionBlock[], n = 96): DieMap {
  const density = new Float64Array(n * n).fill(0.02)
  const lineWidthUm = new Float64Array(n * n).fill(1)
  const regionIndex = new Int16Array(n * n).fill(-1)
  const regions = blocks.map((b) => b.region)
  blocks.forEach((b, k) => {
    for (let y = Math.max(0, b.y0); y < Math.min(n, b.y1); y++) {
      for (let x = Math.max(0, b.x0); x < Math.min(n, b.x1); x++) {
        const i = y * n + x
        density[i] = b.region.metalDensity
        lineWidthUm[i] = b.region.lineWidthUm
        regionIndex[i] = k
      }
    }
  })
  return { n, cellUm: (DIE_MM * 1000) / n, density, lineWidthUm, regionIndex, regions }
}

/**
 * The default floorplan: the closed form's regions as blocks in a row across
 * the die's middle, field around them - the same test die, given coordinates.
 */
export function dieFromRegions(layout: PatternRegion[], n = 96): DieMap {
  const blockW = Math.floor(n / (layout.length + 1))
  const gap = Math.floor(blockW / (layout.length + 1))
  const y0 = Math.floor(n / 2 - blockW / 2)
  return dieFromBlocks(
    layout.map((region, k) => ({
      region,
      x0: gap + k * (blockW + gap),
      y0,
      x1: gap + k * (blockW + gap) + blockW,
      y1: y0 + blockW,
    })),
    n,
  )
}

// ---------------------------------------------------------------- results

export interface PlanarizeRegionResult {
  region: PatternRegion
  clearedAtS: number
  residueNm: number
  dishingNm: number
  erosionNm: number
}

export interface PlanarizeResult {
  die: DieMap
  /** Final surface height per cell, relative to the trench-top plane [nm]. */
  surface: Float64Array
  /** Copper still bridging per cell [nm]. Zero everywhere on a good die. */
  residue: Float64Array
  /** When each cell cleared [s]. Infinity where it never did. */
  clearedAt: Float64Array
  /** Sub-grid dishing per cell [nm]. */
  dishing: Float64Array
  regions: PlanarizeRegionResult[]
  endpointS: number
  totalTimeS: number
  /** Worst-to-best spread of the finished surface across the die [nm]. */
  surfaceRangeNm: number
  /** Cells still carrying metal at the end. */
  shortedCells: number
  /** Centre of the worst residue patch, die fraction 0..1, or null. */
  shortAt: { x: number; y: number } | null
  /** Die-scale step height against time, for the planarisation curve. */
  stepHistory: Array<{ t: number; stepNm: number }>
}

// ----------------------------------------------------------------- solver

/** Three box blurs approximate a Gaussian and cost O(n) regardless of width. */
function boxBlur(src: Float64Array, dst: Float64Array, n: number, r: number): void {
  const tmp = new Float64Array(n * n)
  const w = 2 * r + 1
  // Horizontal.
  for (let y = 0; y < n; y++) {
    let acc = 0
    for (let x = -r; x <= r; x++) acc += src[y * n + clamp(x, 0, n - 1)]
    for (let x = 0; x < n; x++) {
      tmp[y * n + x] = acc / w
      acc += src[y * n + clamp(x + r + 1, 0, n - 1)] - src[y * n + clamp(x - r, 0, n - 1)]
    }
  }
  // Vertical.
  for (let x = 0; x < n; x++) {
    let acc = 0
    for (let y = -r; y <= r; y++) acc += tmp[clamp(y, 0, n - 1) * n + x]
    for (let y = 0; y < n; y++) {
      dst[y * n + x] = acc / w
      acc += tmp[clamp(y + r + 1, 0, n - 1) * n + x] - tmp[clamp(y - r, 0, n - 1) * n + x]
    }
  }
}

export function planarize(
  recipe: CmpRecipe,
  condition: CmpCondition = NOMINAL_CMP_CONDITION,
  die: DieMap,
  /** Pad planarisation length [mm]. Exposed so the harness can prove it IS one. */
  planarizationMm = PLANARIZATION_LENGTH_MM,
): PlanarizeResult {
  const { n, density, lineWidthUm, regionIndex, regions } = die
  const cells = n * n

  const kp = prestonCoefficient(condition)
  const selectivity = selectivityOf(condition)
  const recessed = recessedMetalFraction(condition)
  // The die sits at the wafer centre; the radial story stays with the closed
  // form, which models the whole wafer.
  const blanketNmS =
    kp * pressureAt(recipe, condition, 0) * relativeVelocity(recipe, 0) * 1e9

  // Initial state. Post-plating the surface follows the pattern: open field
  // stands a full step proud, a fully dense block sits at the overburden.
  const cu = new Float64Array(cells)
  const subStep = new Float64Array(cells)
  const oxDrop = new Float64Array(cells)
  const dishing = new Float64Array(cells)
  const clearedAt = new Float64Array(cells).fill(Infinity)
  for (let i = 0; i < cells; i++) {
    cu[i] = recipe.overburdenNm + recipe.incomingStepNm * (1 - density[i])
    // The line/space texture inside the cell - below pad resolution, handled
    // by the closed form's own contact-fraction law.
    subStep[i] = density[i] > 0.04 ? recipe.incomingStepNm : 0
  }

  const surface = new Float64Array(cells)
  const envelope = new Float64Array(cells)
  const sigmaCells = Math.max(1, Math.round((planarizationMm * 1000) / die.cellUm / 2))

  const stepHistory: Array<{ t: number; stepNm: number }> = []
  const dt = 0.25
  const maxSteps = 4000
  let endpointS = -1
  let clearedArea = 0
  const endpointArea = ENDPOINT_CLEARED_FRACTION * cells
  let t = 0
  let overpolishUntil = Infinity

  for (let step = 0; step < maxSteps; step++) {
    // Current mean surface per cell, for the pad to feel.
    for (let i = 0; i < cells; i++) surface[i] = cu[i] > 0 ? cu[i] : -oxDrop[i]

    // The pad: its reference face is the surface it cannot follow.
    boxBlur(surface, envelope, n, sigmaCells)

    // Contact pressure under fixed total force: high spots carry more.
    let wSum = 0
    for (let i = 0; i < cells; i++) {
      const excess = (surface[i] - envelope[i]) / COMPLIANCE_NM
      envelope[i] = Math.max(0.05, 1 + excess) // reuse as weight
      wSum += envelope[i]
    }
    const norm = cells / wSum

    if (stepHistory.length === 0 || step % 8 === 0) {
      let hi = -Infinity
      let lo = Infinity
      for (let i = 0; i < cells; i++) {
        if (surface[i] > hi) hi = surface[i]
        if (surface[i] < lo) lo = surface[i]
      }
      stepHistory.push({ t, stepNm: hi - lo })
    }

    for (let i = 0; i < cells; i++) {
      const local = blanketNmS * envelope[i] * norm * dt
      if (cu[i] > 0) {
        if (subStep[i] > 0) {
          // Within-cell step: the raised fraction carries the whole cell load.
          const f = clamp(1 - density[i], 0.02, 1)
          subStep[i] = Math.max(0, subStep[i] - local / f)
          cu[i] = Math.max(0, cu[i] - local)
        } else {
          cu[i] = Math.max(0, cu[i] - local)
        }
        if (cu[i] <= 0) {
          clearedAt[i] = t + dt
          clearedArea++
        }
      } else {
        // Cleared: composite surface. Oxide is inhibited, recessed metal is
        // protected - the same two facts that give the closed form its
        // selectivity, applied per cell per step.
        const rOx = local / selectivity
        const rMetal = local * recessed
        const composite = density[i] * rMetal + (1 - density[i]) * rOx
        oxDrop[i] += composite
        dishing[i] += Math.max(0, padReach(lineWidthUm[i]) * (rMetal - rOx))
      }
    }

    t += dt
    if (endpointS < 0 && clearedArea >= endpointArea) {
      endpointS = t
      overpolishUntil = t + endpointS * (recipe.overpolishPct / 100)
    }
    if (t >= overpolishUntil) break
  }
  const totalTimeS = Math.min(t, overpolishUntil)
  if (endpointS < 0) endpointS = totalTimeS

  // Final bookkeeping.
  for (let i = 0; i < cells; i++) surface[i] = cu[i] > 0 ? cu[i] : -oxDrop[i]
  let shortedCells = 0
  let worstResidue = 0
  let shortAt: { x: number; y: number } | null = null
  let hi = -Infinity
  let lo = Infinity
  for (let i = 0; i < cells; i++) {
    if (cu[i] > 0) {
      shortedCells++
      if (cu[i] > worstResidue) {
        worstResidue = cu[i]
        shortAt = { x: (i % n) / n, y: Math.floor(i / n) / n }
      }
    }
    const top = surface[i]
    const bottom = top - dishing[i]
    if (top > hi) hi = top
    if (bottom < lo) lo = bottom
  }

  const regionResults: PlanarizeRegionResult[] = regions.map((region, k) => {
    let clearedLast = 0
    let residue = 0
    let dish = 0
    let ox = 0
    let fieldOx = 0
    let fieldCount = 0
    let count = 0
    for (let i = 0; i < cells; i++) {
      if (regionIndex[i] === k) {
        count++
        if (clearedAt[i] > clearedLast) clearedLast = clearedAt[i]
        if (cu[i] > residue) residue = cu[i]
        if (dishing[i] > dish) dish = dishing[i]
        ox += oxDrop[i]
      } else if (regionIndex[i] === -1) {
        fieldOx += oxDrop[i]
        fieldCount++
      }
    }
    return {
      region,
      clearedAtS: clearedLast,
      residueNm: residue,
      dishingNm: dish,
      // Erosion the way it is measured: the array's surface below the field's.
      erosionNm: Math.max(
        0,
        (count > 0 ? ox / count : 0) - (fieldCount > 0 ? fieldOx / fieldCount : 0),
      ),
    }
  })

  return {
    die,
    surface,
    residue: cu,
    clearedAt,
    dishing,
    regions: regionResults,
    endpointS,
    totalTimeS,
    surfaceRangeNm: hi - lo,
    shortedCells,
    shortAt,
    stepHistory,
  }
}
